import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DollarSign, Calendar, Package, CreditCard, ArrowUpRight, ArrowDownRight, ArrowUp, ArrowDown } from 'lucide-react';
import toast from 'react-hot-toast';

const Transactions = () => {
  const { user, isAdmin } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, purchases, topups
  const [isAdminView, setIsAdminView] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await fetch('/api/transactions', {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setTransactions(data.data.transactions || []);
        setIsAdminView(data.data.isAdmin || false);
      } else {
        toast.error(data.message || 'Failed to fetch transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchase':
        return <ArrowDown className="h-5 w-5 text-red-400" />;
      case 'topup':
        return <ArrowUp className="h-5 w-5 text-green-400" />;
      default:
        return <DollarSign className="h-5 w-5 text-gray-400" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'purchase':
        return '#ef4444'; // red
      case 'topup':
        return '#22c55e'; // green
      default:
        return '#9ca3af'; // gray
    }
  };

  const formatDate = (dateString) => {
    // Database stores in UTC, need to handle properly
    // If the string doesn't have timezone info, treat it as UTC
    let date;
    if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('T')) {
      // Already has timezone info
      date = new Date(dateString);
    } else {
      // Assume UTC and append Z
      date = new Date(dateString + 'Z');
    }
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const filteredTransactions = transactions.filter(t => {
    if (filter === 'all') return true;
    if (filter === 'orders') return t.type === 'purchase';
    return t.type === filter.replace('s', ''); // topups -> topup
  });

  const totalSpent = transactions
    .filter(t => t.type === 'purchase')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const totalTopups = transactions
    .filter(t => t.type === 'topup')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <div className="py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-100">Transactions</h1>
        <p className="text-sm text-gray-400 mt-1">View your complete balance & order history</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Current Balance */}
        <div className="rounded-lg p-6 border" style={{ backgroundColor: '#1a1a1a', borderColor: '#4a4a4a' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Current Balance</p>
              <p className="text-2xl font-bold text-gray-100 mt-1">${user?.balance?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: '#3b198e20' }}>
              <DollarSign className="h-6 w-6" style={{ color: '#3b198e' }} />
            </div>
          </div>
        </div>

        {/* Total Top-ups */}
        <div className="rounded-lg p-6 border" style={{ backgroundColor: '#1a1a1a', borderColor: '#4a4a4a' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Total Top-ups</p>
              <p className="text-2xl font-bold text-green-400 mt-1">${totalTopups.toFixed(2)}</p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: '#22c55e20' }}>
              <ArrowUp className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </div>

        {/* Total Spent */}
        <div className="rounded-lg p-6 border" style={{ backgroundColor: '#1a1a1a', borderColor: '#4a4a4a' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Total Spent</p>
              <p className="text-2xl font-bold text-red-400 mt-1">${totalSpent.toFixed(2)}</p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: '#ef444420' }}>
              <ArrowDown className="h-6 w-6 text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: filter === 'all' ? '#3b198e' : '#1a1a1a',
            color: filter === 'all' ? '#ffffff' : '#9ca3af',
            border: `1px solid ${filter === 'all' ? '#3b198e' : '#4a4a4a'}`
          }}
        >
          All Transactions
        </button>
        <button
          onClick={() => setFilter('orders')}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: filter === 'orders' ? '#3b198e' : '#1a1a1a',
            color: filter === 'orders' ? '#ffffff' : '#9ca3af',
            border: `1px solid ${filter === 'orders' ? '#3b198e' : '#4a4a4a'}`
          }}
        >
          Orders
        </button>
        <button
          onClick={() => setFilter('topups')}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: filter === 'topups' ? '#3b198e' : '#1a1a1a',
            color: filter === 'topups' ? '#ffffff' : '#9ca3af',
            border: `1px solid ${filter === 'topups' ? '#3b198e' : '#4a4a4a'}`
          }}
        >
          Top-ups
        </button>
      </div>

      {/* Transactions List */}
      <div className="rounded-lg border" style={{ backgroundColor: '#1a1a1a', borderColor: '#4a4a4a' }}>
        {loading ? (
          <div className="p-8 text-center text-gray-400">
            Loading transactions...
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="h-12 w-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No transactions found</p>
            <p className="text-sm text-gray-500 mt-1">Your transaction history will appear here</p>
          </div>
        ) : (
          <div className="space-y-0">
            {filteredTransactions.map((transaction, index) => (
              <div key={index}>
                <div className="p-4 hover:bg-opacity-50 transition-colors" style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: '#0a0a0a' }}>
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-100">
                        {transaction.description || (transaction.type === 'purchase' ? 'Product Purchase' : 'Balance Top-up')}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="h-3 w-3 text-gray-500" />
                        <p className="text-xs text-gray-500">{formatDate(transaction.created_at)}</p>
                        {isAdminView && transaction.user_email && (
                          <>
                            <span className="text-gray-600">•</span>
                            <p className="text-xs text-gray-500">{transaction.user_email}</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold" style={{ color: getTransactionColor(transaction.type) }}>
                      {transaction.type === 'purchase' ? '-' : '+'}${transaction.amount?.toFixed(2) || '0.00'}
                    </p>
                    {transaction.payment_method && (
                      <p className="text-xs text-gray-500 mt-1 capitalize">{transaction.payment_method}</p>
                    )}
                  </div>
                </div>
                </div>
                {index < filteredTransactions.length - 1 && (
                  <div className="px-4">
                    <div style={{ 
                      height: '1px', 
                      background: 'linear-gradient(to right, transparent, #4a4a4a 10%, #4a4a4a 90%, transparent)',
                      opacity: 0.5
                    }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Transactions;
